// Catalog.ID Client — API Type Definitions
// ============================================================================
// Request/response types matching the server's contracts.

// ============================================================================
// Shared
// ============================================================================

export interface PublicKeyInput {
    keyId: string;
    algorithm: string;
    publicKey: string;
}

export interface PublicKeyInfo {
    keyId: string;
    algorithm: string;
    publicKey: string;
    keyIndex: number;
    status?: string;
}

export interface WrappedKeyInput {
    keyId: string;
    keyIndex: number;
    wrappedKey: string;
}

// ============================================================================
// Account
// ============================================================================

export interface KeyBagEntryInput {
    keyId: string;
    keyIndex: number;
    algorithm: string;
    encryptedPrivateKey: string;
    nonce: string;
    status?: string;
    /** A signed `keybag-entry` step-up challenge (cid-encrypted-storage §10
     *  item 7). REQUIRED by the single-entry `POST /api/account/keybag/entries`
     *  — `addKeyBagEntry` takes it as its own argument and fills this in — and
     *  ignored inside the arrays the register and whole-bag-replace bodies
     *  carry, which are gated on their own. Optional here because the same
     *  interface types those arrays. */
    challengeSignature?: string;
}

/** Step 1 of registration. Reserves a CID against the payment proof so the client
 *  can compute Argon2id(password, SHA-256(cid)) before calling /register. */
export interface BeginRegisterRequest {
    /** Omitted when redeeming a voucher — the two are mutually exclusive and the
     *  server refuses a request carrying both (registration-restructure §7.2). */
    paymentProofToken?: string;
    nonce?: string;
    /** 64 lowercase hex, `deriveVoucherSecrets(code).lookupIdHex`, presented
     *  instead of a payment proof (registration-wallets §4.1). The first begin
     *  reserves the voucher and binds the CID it returns; re-begin yields the
     *  same CID. The code stays on this device — it opens the lock holding the
     *  voucher's bits, and the node must never be able to. */
    voucherLookupId?: string;
    /** A PERSONAL invitation, addressed by the lookup id derived from its `INV-`
     *  code (registration-restructure §14.3). The invitation names the voucher it
     *  reserved, so the CID it returns is anchored on that voucher exactly as a
     *  typed code would be — and a re-begin yields the same CID. Exactly one of
     *  the three authorizations may be present. */
    invitationLookupId?: string;
}

export interface BeginRegisterResponse {
    cid: string;
    expiresAt: string;
}

export interface RegisterRequest {
    /** The CID returned by /register/begin for this payment proof. */
    cid: string;
    authHash: string;
    keySalt: string;
    publicKeys: PublicKeyInput[];
    keyBagEntries: KeyBagEntryInput[];
    // ES256 JWT minted by the payment Worker after Stripe confirms payment.
    // Omitted when redeeming a voucher.
    paymentProofToken?: string;
    // Same nonce that was bound into the proof token; the server requires both to match.
    nonce?: string;
    /** Voucher lookup id presented instead of a payment proof; the row it names
     *  is consumed on success. */
    voucherLookupId?: string;
    /** A personal invitation (registration-restructure §14.3). Marked redeemed and
     *  bound to the new account here, which is what lets the node hand the
     *  validator the digest of the address it fixed. */
    invitationLookupId?: string;
}

export interface RegisterResponse {
    cid: string;
    sessionToken: string;
    /** Public session handle (the `sid`) — held by SessionState, surfaced to EFS. */
    sessionId: string;
}

export interface LoginRequest {
    cid: string;
    authHash: string;
}

export interface LoginResponse {
    sessionToken: string;
    /** Public session handle (the `sid`) — held by SessionState, surfaced to EFS. */
    sessionId: string;
    cid: string;
    status: string;
    accountType: string;
    /** Operator powers on THIS node: the org bound to its code (cid-governance D12). */
    isOperator: boolean;
    /** Every operator code bound to the account's organization, on any node (D12). */
    operatorCodes: string[];
}

/**
 * What `POST /api/account/login` answers when the account's Authenticator Key
 * login gate is on (registration-restructure §15.5): no session, a five-minute
 * pre-auth token, and a demand for a code. `status` is the discriminator — a
 * successful login carries a `sessionToken` and never this value.
 */
export interface TotpRequiredResponse {
    status: 'totp_required';
    preAuthToken: string;
    ttlSeconds: number;
}

/** A login answer is one or the other; `totp_required` is the only status a
 *  session-less success can carry. */
export type LoginOrTotpResponse = LoginResponse | TotpRequiredResponse;

export function isTotpRequired(response: LoginOrTotpResponse): response is TotpRequiredResponse {
    return (response as TotpRequiredResponse).status === 'totp_required'
        && typeof (response as TotpRequiredResponse).preAuthToken === 'string';
}

/** `POST /api/account/totp/reveal` — the only path by which a stored
 *  Authenticator Key leaves the node. */
export interface TotpRevealResponse {
    /** Base32, 32 characters — what the sheet prints in groups of four. */
    secret: string;
    /** The `otpauth://` URI, for the QR. */
    uri: string;
    /** Whether the login gate is currently on. */
    enabled: boolean;
    issuedAt: string;
}

export interface KeyBagResponse {
    keySalt: string;
    entries: {
        keyId: string;
        keyIndex: number;
        algorithm: string;
        encryptedPrivateKey: string;
        nonce: string;
        status: string;
    }[];
}

export interface ReplaceKeyBagRequest {
    entries: KeyBagEntryInput[];
    keySalt?: string;
    /** Signed "replace-keybag" step-up challenge (main_signing_key custody proof). */
    challengeSignature?: string;
}

// ============================================================================
// Device
// ============================================================================

export interface DeviceSetupRequest {
    deviceToken: string;
    deviceEncryptionKey: string;
    accessMode: string;
    pin?: string;
    pinLength?: number;
    /** Client-encrypted device name envelope (crypto/device-label.ts). Opaque
     *  to the server, ≤ 8192 chars. Sent with labelKeyIndex or not at all. */
    label?: string;
    /** keyIndex of the attr_encrypt public key `label` was wrapped to. */
    labelKeyIndex?: number;
    /**
     * Require the Authenticator Key as a second unlock factor on this device
     * (device-setup-restructure §3). Orthogonal to `accessMode`: the four modes
     * are the product of the two, `open`/`pin` × false/true.
     *
     * When true the node demands a LIVE `code` before it will write the row —
     * a member who never scanned the QR must not be able to lock a device into
     * a factor they do not hold (§1 item 5). Refusals: 400
     * `"This account has no Authenticator Key"`, 403 `origin_only` off the
     * account's origin node, 400 `"Invalid authenticator code"`, 429 with
     * `retryAfterSeconds`.
     */
    totpRequired?: boolean;
    /** Six digits from the member's authenticator app. Verified LAST, right
     *  before the write, so a request refused for any other reason leaves the
     *  code unspent. */
    code?: string;
}

export interface DeviceSetupResponse {
    deviceId: number;
}

/**
 * `POST /api/device/reset` — the locked-device recovery body. Same shape as
 * setup PLUS a `device-manage` step-up (auth/step-up.ts): reset clears a PIN
 * lockout and overwrites the device encryption key, so the server demands proof
 * of main_signing_key possession rather than a session token alone. `setup`
 * deliberately carries no signature — it only creates a row with the caller's
 * own key, and registration finalize calls it right after a key replacement.
 */
export interface DeviceResetRequest extends DeviceSetupRequest {
    challengeSignature?: string;
}

/**
 * `POST /api/device/unlock` — the factors this call carries. Send only what the
 * node asked for: a `factors_required` refusal names exactly what is still
 * missing, and a gate pass (a session created through the login code gate)
 * covers the code factor ONCE, but only for a code-less call. See
 * auth/device-unlock.ts.
 */
export interface DeviceUnlockRequest {
    deviceToken: string;
    pin?: string;
    /** Six digits, when the device row requires the Authenticator factor. */
    code?: string;
}

export interface DeviceUnlockResponse {
    deviceEncryptionKey: string;
}

export interface ChangePinRequest {
    deviceToken: string;
    accessMode: string;
    pin?: string;
    pinLength?: number;
    deviceEncryptionKey?: string;
    /** `device-manage` step-up (auth/step-up.ts). Required in practice — the
     *  server refuses the call without it, because flipping a device to `open`
     *  while keeping its stored key would hand the key out unlocked. */
    challengeSignature?: string;
    /** See {@link DeviceSetupRequest.totpRequired}. */
    totpRequired?: boolean;
    /** See {@link DeviceSetupRequest.code}. On this endpoint it serves BOTH
     *  purposes, because at most one code is ever needed per change (§15): the
     *  live code that proves the member holds the key when an Authenticator
     *  mode is chosen or kept, and the code being REMOVED when it is turned
     *  off.
     *
     *  "Keep the PIN" (device-setup-restructure §6.5): with `accessMode` equal
     *  to the row's CURRENT mode, `pin` and `pinLength` may be omitted and the
     *  stored hash is kept. That is how the Login code step turns the
     *  Authenticator factor on while holding the member's phone but not their
     *  PIN. Omitting `pin` while switching TO `pin` is still a 400 — there is
     *  nothing to keep. */
    code?: string;
    /** The PIN the row holds TODAY — the downgrade gate (§15, decided
     *  2026-09-18). Required when the change REMOVES or REPLACES the PIN
     *  (mode away from `pin`, a new `pin`, or a different `pinLength`), never
     *  for "keep the PIN", and meaningless on a device that has none. A call
     *  short of it answers `400 { error: "factors_required", factors: [...] }`
     *  and writes nothing; a wrong one is `401 Wrong PIN` and COUNTS, because
     *  the node verifies it with the same row-locked verifier `unlock` uses.
     *  See auth/device-mode-change.ts. */
    currentPin?: string;
}

export interface RemoveDeviceRequest {
    deviceToken: string;
}

export interface DeviceInfo {
    deviceId: number;
    accessMode: string;
    /** Whether this device also asks for an authenticator code to unlock. With
     *  `accessMode` this is the device's mode: the four of §3 are the product
     *  of the two. */
    totpRequired: boolean;
    isLocked: boolean;
    creationTime: string;
    lastUsedAt: string;
    /** Client-encrypted device name envelope, or null if the device was never
     *  named. Decrypt with crypto/device-label.ts decryptDeviceLabel(). */
    label: string | null;
    /** keyIndex of the attr_encrypt key `label` was wrapped to; null with label. */
    labelKeyIndex: number | null;
}

/** Body of PUT /api/device/{deviceId}/label. */
export interface UpdateDeviceLabelRequest {
    label: string;
    labelKeyIndex: number;
}

export interface AccountInfo {
    cid: string;
    accountType: string;
    status: string;
    /** Operator powers on THIS node: the org bound to its code (cid-governance D12). */
    isOperator: boolean;
    /** Every operator code bound to the account's organization, on any node (D12). */
    operatorCodes: string[];
    creationTime: string;
    organizationCid: string | null;
    principalCid: string | null;
    roles: string[] | null;
    /** Public session handle of the current session — lets SessionState
     *  restore the `sid` after a page reload. */
    sessionId: string;
    /** Authenticator Key login gate (registration-restructure §15.6). Carried
     *  here so Settings can show the status without a step-up — `reveal` is the
     *  only way to the SECRET, and it costs one. */
    totpEnabled?: boolean;
    /** A key exists on the node at all (false for accounts registered before
     *  round 8). */
    totpKeyIssued?: boolean;
    /** When a code first confirmed the current key; null while the gate is off. */
    totpConfirmedAt?: string | null;
}

// ============================================================================
// Account Termination & Key Rotation
// ============================================================================

export interface TerminateRequest {
    erasePublicClaims?: boolean;
    /** Step-up proof: hybrid signature from main_signing_key over
     *  "terminate\n{cid}\n{base64(challenge)}". Required by the server — terminate
     *  is irreversible, so a session token alone is not sufficient. For an
     *  organization's termination the purpose is `org-terminate`, signed by the
     *  terminating owner (docs/cid-governance.md M15). */
    challengeSignature?: string;
    /** The acknowledgement that the identity's live agents end with it
     *  (docs/cid-governance.md M15); without it a principal of live agents gets
     *  409 `principal_has_live_agents` (auth/principal-termination.ts). */
    terminateAgents?: boolean;
}

/** Server response to POST /api/account/step-up/challenge. */
export interface StepUpChallengeResponse {
    challenge: string;
    ttlSeconds: number;
}

export interface TerminateResponse {
    terminated: boolean;
    cid: string;
    publicClaimsPreserved: boolean;
}

export interface RequestUnlockRequest {
    cid: string;
}

export interface ApproveUnlockRequest {
    cid: string;
}

export interface PendingUnlockAccount {
    cid: string;
    accountType: string;
    lockedAt: string;
}

export interface RotateKeysRequest {
    newAttrEncryptPublicKey: string;
    newMsgEncryptPublicKey: string;
    /** New efs_encrypt public key. Optional on the wire; the recovery client
     *  supplies it so a compromise-driven lockdown rotates the EFS key too. */
    newEfsEncryptPublicKey?: string;
    publicKeyAlgorithm: string;
    keyBagEntries: KeyBagEntryInput[];
    keySalt?: string;
    /** Canonical-JSON SignatureWrapper (Hybrid Ed25519 + ML-DSA-65) over
     *  "rotate-keys\n{cid}\n{base64(challenge)}" produced with the account's
     *  current main_signing_key. Proves supersecret knowledge (H-3). */
    challengeSignature: string;
}

export interface RotateKeysChallengeResponse {
    challenge: string;
    ttlSeconds: number;
}

export interface RotateKeysResponse {
    rotated: boolean;
    claimKeysRewrapped: number;
}

export interface TransferProfileRequest {
    oldCid: string;
    newCid: string;
}

// ============================================================================
// Claims
// ============================================================================

export interface CreateClaimRequest {
    claimType: string;
    encryptedValue: string;
    nonce: string;
    encryptedKey: string;
    encryptedLabel?: string;
    labelNonce?: string;
}

export interface CreateClaimResponse {
    claimUuid: string;
}

export interface ClaimSummary {
    claimUuid: string;
    claimType: string;
    validationStatus: string;
    encryptedLabel: string | null;
    labelNonce: string | null;
    revokedAt: string | null;
    revokedReason: string | null;
    creationTime: string;
}

export interface ClaimKeyEntry {
    recipientType: string;
    recipientCid: string | null;
    encryptedKey: string;
    keyIndex: number;
}

export interface CertificateEntry {
    certificateData: string;
    validationType: string;
    issuedAt: string;
    // null for non-expiring certificates (e.g. registry_signing_key per WP §8.1).
    expiresAt: string | null;
    isActive: boolean;
}

export interface ClaimDetail {
    claimUuid: string;
    claimType: string;
    encryptedValue: string;
    nonce: string;
    validationStatus: string;
    encryptedLabel: string | null;
    labelNonce: string | null;
    revokedAt: string | null;
    revokedReason: string | null;
    creationTime: string;
    keys: ClaimKeyEntry[];
    certificates: CertificateEntry[];
}

export interface UpdateClaimRequest {
    encryptedValue: string;
    nonce: string;
}

export interface ShareClaimRequest {
    recipientCid: string;
    encryptedKey: string;
}

export interface RevokeClaimRequest {
    reason: string;
}

/**
 * Per-recipient unshare (chat migration plan §4). Deletes exactly one
 * `claimkey` row and federates it on the `claimkey delete` op-log wire.
 * The server refuses the owner's own CID (the `'self'` wrap lives in the same
 * table — deleting it orphans the claim's symmetric key permanently) and
 * operator review keys (stored as `recipienttype='share'` and
 * indistinguishable from user shares client-side, so the refusal has to come
 * from the server).
 */
export interface RevokeShareRequest {
    recipientCid: string;
}

export interface SharedClaimSummary {
    claimUuid: string;
    claimType: string;
    encryptedValue: string;
    nonce: string;
    encryptedKey: string;
    encryptedLabel: string | null;
    labelNonce: string | null;
    ownerCid: string;
    creationTime: string;
}

export interface ValidateClaimRequest {
    validationType: string;
    encryptedKey: string;
    /**
     * Optional (registration-restructure §14.2): the fixed-value salt this
     * account derived from its invitation code, sealed to the validator's hybrid
     * KEM public key exactly as `encryptedKey` is.
     *
     * The node cannot open it and holds no salt of its own — that is the point:
     * without a salt in its database, nobody holding that database can
     * dictionary-scan the pending invitation hashes for a known address. The
     * node forwards this blob unchanged, and the validator unseals it to
     * recompute the digest it was asked to match.
     */
    encryptedValueSalt?: string;
}

export interface ValidateClaimResponse {
    jobId: string;
    challenge?: string; // Registry key validation: the challenge token to sign
    // Domain: the token to place at .well-known/catalogid.txt.
    // Phone: the opaque job handle to submit alongside the SMS code.
    verificationToken?: string;
}

export interface RegistryKeyVerifyResponse {
    success: boolean;
    certificate?: string;
}

// ============================================================================
// Public Claims
// ============================================================================

export interface PublicClaimSummary {
    publicClaimId: number;
    claimUuid: string;
    claimType: string;
    publicValue: string;
    status: string;
    rejectionReason: string | null;
    publishedAt: string | null;
    /**
     * Key class on the `registry_signing_key` track (chat migration §3):
     * `machine` for a per-machine Registry signing key, `chat_root` /
     * `mail_root` for an account-level chat / mail root. Meaningless — and
     * always `machine` — on every other claim type. Optional here only so a
     * server predating the column does not make the pane fail to render.
     */
    keyClass?: string;
    creationTime: string;
}

export interface PublishClaimRequest {
    operatorKeys?: OperatorKeyInput[];
    /**
     * Key class to publish under (chat migration §3). Omitted ⇒ the server
     * defaults to `machine`. Declared HERE, at publish time, and immutable
     * afterwards — there is no edit path, so a chat root published without it
     * would silently carry EFS retrieval and OIDC machine-key authority.
     */
    keyClass?: string;
}

export interface OperatorKeyInput {
    operatorCid: string;
    encryptedKey: string;
}

export interface OperatorInfo {
    cid: string;
    publicKey: string;
}

export interface PendingPublicClaim {
    publicClaimId: number;
    claimUuid: string;
    claimType: string;
    ownerCid: string;
    ownerAccountType: string;
    creationTime: string;
}

export interface ReviewPublicClaimRequest {
    approved: boolean;
    publicValue?: string;
    rejectionReason?: string;
    // Note: operatorSignature is computed server-side using the node signing key
    // and is not a request input. See identity/plans/roadmap.md Phase 11d.
}

// ============================================================================
// Profile
// ============================================================================

export interface PublicProfileClaim {
    claimType: string;
    publicValue: string;
    operatorSignature: string;
    publishedAt: string;
}

export interface PublicProfile {
    cid: string;
    accountType: string;
    terminated?: boolean;
    claims: PublicProfileClaim[];
    organizationCid?: string;
    principalCid?: string;
}

// ============================================================================
// Organization
// ============================================================================

/** Step 1 of organization registration. Reserves both the organization CID and the
 *  founding delegate CID against the payment proof so the client can compute
 *  Argon2id(password, SHA-256(cid)) for both before /register. */
export interface BeginOrgRegisterRequest {
    /** Omitted when redeeming a voucher (registration-restructure §7.2). */
    paymentProofToken?: string;
    nonce?: string;
    /** 64 lowercase hex, `deriveVoucherSecrets(code).lookupIdHex`. */
    voucherLookupId?: string;
    /** An ORGANIZATION invitation (registration-restructure §14.3). */
    invitationLookupId?: string;
    /** The domain this organization will claim, in PLAINTEXT. Required with
     *  `invitationLookupId` when the invitation fixes a domain, and compared
     *  against it — the domain CLAIM is encrypted to the org's key, so this is
     *  the only form the node can check, and a domain is public anyway. */
    domain?: string;
}

export interface BeginOrgRegisterResponse {
    orgCid: string;
    delegateCid: string;
    expiresAt: string;
}

export interface OrganizationRegisterRequest {
    /** CIDs returned by /register/begin for this payment proof. */
    orgCid: string;
    delegateCid: string;
    orgAuthHash: string;
    orgKeySalt: string;
    orgPublicKeys: PublicKeyInput[];
    orgKeyBagEntries: KeyBagEntryInput[];
    delegateAuthHash: string;
    delegateKeySalt: string;
    delegatePublicKeys: PublicKeyInput[];
    delegateKeyBagEntries: KeyBagEntryInput[];
    wrappedOrgKeys: WrappedKeyInput[];
    domainClaim: DomainClaimInput;
    // ES256 JWT minted by the payment Worker after Stripe confirms payment.
    // Omitted when redeeming a voucher.
    paymentProofToken?: string;
    // Same nonce that was bound into the proof token; the server requires both to match.
    nonce?: string;
    /** Voucher lookup id presented instead of a payment proof; consumed on success. */
    voucherLookupId?: string;
    /** An organization invitation (registration-restructure §14.3). */
    invitationLookupId?: string;
    /** The claimed domain in plaintext — re-compared against the invitation here. */
    domain?: string;
}

export interface DomainClaimInput {
    claimUuid: string;
    encryptedValue: string;
    nonce: string;
    encryptedKey: string;
    validatorEncryptedKey?: string;
}

export interface OrganizationRegisterResponse {
    orgCid: string;
    delegateCid: string;
    sessionToken: string;
    /** Public session handle (the `sid`) — held by SessionState, surfaced to EFS. */
    sessionId: string;
    domainVerificationToken?: string;
}

// ============================================================================
// Delegate
// ============================================================================

export interface ActivateDelegateRequest {
    /** Signed invitation (registration-restructure §14.2): the 64-hex lookup id
     *  derived from the code on the card. There is no invitation id or bearer
     *  secret on this wire any more — and, since M20, no bearer token either. */
    lookupId?: string;
    /** The CID `begin` drew for this invitation — the node checks its origin
     *  before its invitation lookup (docs/cid-governance.md M7a). */
    delegateCid: string;
    authHash: string;
    keySalt: string;
    publicKeys: PublicKeyInput[];
    keyBagEntries: KeyBagEntryInput[];
}

export interface ActivateDelegateResponse {
    delegateCid: string;
    sessionToken: string;
    /** Public session handle (the `sid`) — held by SessionState, surfaced to EFS. */
    sessionId: string;
}

export interface CompleteDelegateRequest {
    wrappedOrgKeys: WrappedKeyInput[];
}

export interface UpdateRolesRequest {
    roles: string[];
    wrappedOrgKeys?: WrappedKeyInput[];
}

/** The delegate's origin's signed word that a delegate's password was right
 *  (docs/cid-governance.md M15a): what `POST /api/delegate/link/attest` answers
 *  and `POST /api/delegate/link` takes. Opaque to the client. */
export interface DelegateLinkAttestation {
    delegateCid: string;
    personalCid: string;
    nodeId: string;
    keySeq: number;
    expiresAt: number;
    signature: string;
}

export interface AttestDelegateLinkRequest {
    delegateCid: string;
    delegateAuthHash: string;
    personalCid: string;
}

export interface LinkDelegateRequest {
    delegateCid: string;
    attestation: DelegateLinkAttestation;
}

export interface UnlinkDelegateRequest {
    delegateCid: string;
}

export interface LinkedIdentity {
    cid: string;
    accountType: string;
    status: string;
    organizationCid?: string;
}

export interface DelegateSummary {
    cid: string;
    roles: string[];
    status: string;
    creationTime: string;
}

// ============================================================================
// Agent
// ============================================================================

export interface CreateAgentRequest {
    label: string;
    agentType: string;
    publicKeys: PublicKeyInput[];
    /** The agent's three private keys, encrypted with the principal's
     *  supersecret-derived AES key. Inserted into the principal's keybag in
     *  the same DB transaction as agent creation, eliminating the gap where
     *  the agent could exist with no recoverable private keys. The server
     *  assembles the full keyId as `agent:{newAgentCid}:{suffix}` after
     *  drawing the agent's CID. */
    principalKeyBagEntries: AgentKeyBagEntryInput[];
}

export interface AgentKeyBagEntryInput {
    /** One of: 'attr_encrypt', 'msg_encrypt', 'agent_signing_key'. */
    suffix: string;
    algorithm: string;
    encryptedPrivateKey: string;
    nonce: string;
}

export interface CreateAgentResponse {
    agentCid: string;
}

export interface AgentSummary {
    cid: string;
    agentType: string;
    agentLabel: string;
    status: string;
    creationTime: string;
}

export interface AgentDetail {
    cid: string;
    agentType: string;
    agentLabel: string;
    status: string;
    principalCid: string;
    creationTime: string;
    publicKeys: PublicKeyInfo[];
}

export interface UpdateAgentRequest {
    label?: string;
    agentType?: string;
}

export interface CreateApiKeyRequest {
    scopes: string[];
    ipAllowList?: string[];
    expiresAt?: string;
}

export interface CreateApiKeyResponse {
    keyIdentifier: string;
    secret: string;
}

export interface ApiKeySummary {
    agentApiKeyId: number;
    keyIdentifier: string;
    scopes: string;
    ipAllowList: string | null;
    creationTime: string;
    expiresAt: string | null;
    isRevoked: boolean;
}

export interface RefreshTokenSummary {
    refreshTokenId: number;
    scopes: string;
    creationTime: string;
    expiresAt: string;
    isRevoked: boolean;
}

// ============================================================================
// Token
// ============================================================================

// Agent token endpoint (/api/token) is a JSON endpoint that follows the rest of
// the Catalog.ID API's camelCase conventions — NOT OAuth form-encoded snake_case.
// The server's TokenRequest record uses PascalCase fields (GrantType, AgentCid,
// Scopes, RefreshToken, Key, Secret), which ASP.NET Core's default serializer
// maps to camelCase on the wire. Likewise the response is an anonymous object
// serialized camelCase.
export interface AgentDelegationRequest {
    grantType: 'agent_delegation';
    agentCid: string;
    scopes: string[];
    persistent?: boolean;
}

export interface RefreshTokenRequest {
    grantType: 'refresh_token';
    refreshToken: string;
}

export interface ApiKeyTokenRequest {
    grantType: 'agent_apikey';
    key: string;
    secret: string;
}

export interface TokenResponse {
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
}

// ============================================================================
// OIDC
// ============================================================================

export interface OidcRelyingParty {
    clientId: string;
    displayName: string;
    displayDomain: string;
    isTrusted?: boolean;
}

export interface OidcRequestedClaims {
    essential: string[];
    optional: string[];
}

export interface OidcExistingConsent {
    scopes: string[];
    claimTypes: string[] | null;
}

export interface OidcPublicClaim {
    type: string;
    value: string;
}

export interface OidcPrivateClaim {
    type: string;
    claimUuid: string;
    validated: boolean;
    validatedAt: string | null;
}

export interface OidcMemberClaims {
    publicClaims: OidcPublicClaim[];
    privateClaims: OidcPrivateClaim[];
}

export interface OidcAuthorizationDetails {
    requestId: string;
    isAuthenticated: boolean;
    relyingParty: OidcRelyingParty;
    requestedScopes: string[];
    requestedClaims: OidcRequestedClaims | null;
    existingConsent: OidcExistingConsent | null;
    memberClaims: OidcMemberClaims;
}

export interface OidcApproveRequest {
    consentedScopes: string[];
    consentedClaimTypes?: string[] | null;
    sdJwt?: { hashes: string[]; claimNames: string[] } | null;
    /** Step-up proof: hybrid signature from main_signing_key over
     *  "oidc-approve\n{cid}\n{base64(challenge)}". Required by the server —
     *  granting a relying party access needs proof of key possession. */
    challengeSignature?: string;
}

export interface OidcApproveResponse {
    code: string;
    state: string;
    redirectUri: string;
    deliveryMode: string;
    /** The node that minted the code (RFC 9207, docs/cid-governance.md M7b). */
    iss: string;
}

export interface OidcDenyResponse {
    redirectUri: string;
    state: string;
    error: string;
    /** The node that answered (RFC 9207). */
    iss: string;
}

export interface OidcConsentSummary {
    authorizationId: number;
    relyingParty: OidcRelyingParty;
    scopes: string[];
    consentedClaimTypes: string[] | null;
    lastAuthTime: string;
    lastRefreshTime: string | null;
}

export interface UpdateConsentRequest {
    scopes: string[];
    consentedClaimTypes: string[] | null;
}

// ============================================================================
// Trust
// ============================================================================

export interface ExpressTrustRequest {
    score: number;
    signature: string;
    commitment: string;
    nonce: string;
    subgraphVisible?: boolean;
}

export interface TrustExpressionSummary {
    targetCid: string;
    score: number;
    subgraphVisible: boolean;
    creationTime: string;
    updatedAt: string | null;
}

export interface ReceivedTrustResponse {
    count: number;
    distribution: Record<number, number>;
    expressions: {
        score: number;
        commitment: string;
        signature: string;
        creationTime: string;
    }[];
}

export interface TrustScoreResponse {
    cid: string;
    score: number;
    tier: number;
    incomingCount: number;
    outgoingCount: number;
    computedAt?: string;
    /**
     * The rating the authenticated caller has given this target (1-5), or
     * null if the caller hasn't rated this target, isn't a personal identity,
     * or is viewing their own profile. Only populated when both viewer and
     * target are personal identities.
     */
    myRating?: number | null;
}

export interface PersonalizedTrustResponse {
    cid: string;
    score: number;
}

export interface MerkleTreeResponse {
    rootHash: string | null;
    entryCount: number;
    scoreDistribution?: Record<string, number>;
    tierBreakdown?: Record<string, number>;
    computedAt?: string;
}

export interface PreTrustedPeer {
    cid: string;
    weight: number;
    creationTime: string;
}

export interface VouchingRequirement {
    claimType: string;
    minEndorsementCount: number;
    minEndorserTier: number;
    operatorCanWaive: boolean;
}

export interface VouchingStatusResponse {
    required: number;
    minEndorserTier?: number;
    current: number;
    met: boolean;
    operatorCanWaive?: boolean;
}

export interface EndorsementSummary {
    endorserCid: string;
    endorsement: boolean;
    signature: string;
    creationTime: string;
}

export interface MyEndorsementSummary {
    claimUuid: string;
    endorsement: boolean;
    creationTime: string;
}

export interface MyClaimEndorsement {
    endorsement: boolean;
    creationTime: string;
}

// ── Verification (verifiable personalized trust) ──

export interface VerifiablePersonalizedResult {
    score: number;
    edges: SubgraphEdge[];
    withheldEdgeCount: number;
    targetReachable: boolean;
    merkleRootHash: string | null;
}

export interface SubgraphEdge {
    sourceAccountId: number;
    targetAccountId: number;
    score: number;
    signature: string;
    commitment: string;
    creationTime: string;
    merkleProof: MerkleInclusionProof | null;
}

export interface MerkleInclusionProof {
    leafHash: string;
    leafIndex: number;
    siblings: MerkleProofSibling[];
    rootHash: string;
}

export interface MerkleProofSibling {
    hash: string;
    isLeft: boolean;
}

// ============================================================================
// Certificates
// ============================================================================

export interface CertificateSummary {
    certificateData: string;
    validationType: string;
    issuedAt: string;
    expiresAt: string;
    isActive: boolean;
}

// ============================================================================
// Account blobs (cid-encrypted-storage §4.2)
// ============================================================================
// A per-account opaque document store, one row per `<moduleId>/<name>` key,
// overwritten in place with a compare-and-set on `generation`. The server sees
// ciphertext only: the body is AES-256-GCM under a key derived from the
// account's `derive_root`, so these shapes carry no plaintext and no key.

/** One row of `GET /api/account/blobs` — metadata only, never the body. */
export interface BlobManifestEntry {
    /** `<moduleId>/<name>`, account-wide: the manifest spans every namespace. */
    blobKey: string;
    /** Monotonic per blob; 0 means "no row" and is what a create must send. */
    generation: number;
    /** Length of the stored base64 ciphertext, not of the plaintext. */
    size: number;
    lastModifiedAt: string;
}

/** `GET /api/account/blobs/{moduleId}/{name}` — the stored ciphertext. */
export interface BlobRecord {
    blobKey: string;
    generation: number;
    /** base64 AES-256-GCM ciphertext (auth tag included). */
    ciphertext: string;
    /** base64 96-bit nonce. */
    nonce: string;
    lastModifiedAt: string;
}

/** The body of a blob write. */
export interface PutBlobBody {
    ciphertext: string;
    nonce: string;
}

/** What the server answers a successful write with. */
export interface PutBlobResult {
    blobKey: string;
    /** The generation now stored — what the NEXT write must send as `If-Match`. */
    generation: number;
}
